import { FormulaParser } from '@datapolarna/formulaparser';
import { createLanguage, type FormulaLanguageDescription } from './formulaLanguage';
import type { ShapeContext } from './shapeContext';

export const formulaParser = new FormulaParser();
formulaParser.addDefaultFunctions();

formulaParser.addFunction<ShapeContext>({
  name: 'same_product_as_saw_line_intake',
  description: 'same_product_as_saw_line_intake(saw_line_name)',
  action(param, model) {
    if (param.length === 0) return '';
    const sawlineName = param[0]!();
    return model.storageArea?.sameProductAsSawLineIntakeFunc(sawlineName) ?? '';
  },
});

formulaParser.addFunction<ShapeContext>({
  name: 'same_product_as_log_sorter_production_order',
  description: 'same_product_as_log_sorter_production_order(log_sorter_name)',
  action(param, model) {
    if (param.length === 0) return '';
    const logSorterName = param[0]!();
    return model.logYard?.sameProductAsLogSorterProductionOrderFunc(logSorterName) ?? '';
  },
});

const languageDescription: FormulaLanguageDescription = {
  variables: [
    {
      name: 'storageArea',
      children: [
        {
          name: 'id',
          details: 'Number',
          info: 'Id of storagearea',
        },
        {
          name: 'name',
          details: 'String',
          info: 'Name of storagearea',
        },
        {
          name: 'type',
          details: 'Number',
          info: 'Type of storagearea',
        },
        {
          name: 'status',
          details: 'Number',
          info: 'Status of storagearea',
        },
        {
          name: 'allowedToPlace',
          details: 'Boolean',
          info: 'Is allowed to place vehicle content in storage area',
        },
        {
          name: 'sameProductsAsVehicle',
          details: 'Boolean',
          info: 'This storage area contains the same products as the vehicle',
        },
        {
          name: 'empty',
          details: 'Boolean',
          info: 'Storage area is empty',
        },
        {
          name: 'note',
          details: 'String',
          info: '',
        },
        {
          name: 'importantNote',
          details: 'Boolean',
          info: '',
        },
        {
          name: 'sameProductAsSawLineIntake',
          details: 'Boolean',
          info: 'This storage area contains the same products as the saw line intake',
        },
        {
          name: 'sameProductAsLogSorterBin',
          details: 'Boolean',
          info: 'This storage area have same content as a log sorter bin',
        },
        {
          name: 'productExistsInOtherStorageAreas',
          details: 'Boolean',
          info: 'This storage area contains products that are also in other storage areas',
        },
        {
          name: 'fillLevel', // Depricated by fillLevelVolume, remove when all customers have changed to fillLevelVolume.
          details: 'Number',
          info: 'Depricated, use fillLevelVolume instead',
        },
        {
          name: 'fillLevelVolume',
          details: 'Number',
          info: 'Current fill level by volume of storage area (0-100)',
        },
        {
          name: 'fillLevelWeight',
          details: 'Number',
          info: 'Current fill level by weight of storage area (0-100)',
        },
      ],
    },

    {
      name: 'logYard',
      children: [
        {
          name: 'allowedProducts',
          details: 'String',
          info: 'Name of all allowed products in storage area',
        },
        {
          name: 'content',
          details: 'String',
          info: 'Name of all products in storage area',
        },
        {
          name: 'firstMoveToDate',
          details: 'String',
          info: 'First move into storage area',
        },
        {
          name: 'lastEmptyDate',
          details: 'String',
          info: 'When the storage area got emptied last',
        },
        {
          name: 'lastInventoryDate',
          details: 'String',
          info: 'When the storage area got inventoried last',
        },
        {
          name: 'firstMoveToDateTime',
          details: 'String',
          info: 'First move into storage area',
        },
        {
          name: 'daysSinceFirstMove',
          details: 'String',
          info: 'How many days since first move',
        },
        {
          name: 'lastEmptyDateTime',
          details: 'String',
          info: 'When the storage area got emptied last',
        },
        {
          name: 'lastInventoryDateTime',
          details: 'String',
          info: 'When the storage area got inventoried last',
        },
      ],
    },

    {
      name: 'bulkStorage',
      children: [
        {
          name: 'allowedProducts',
          details: 'String',
          info: 'Name of all allowed products in storage area',
        },
        {
          name: 'content',
          details: 'String',
          info: 'Name of all products in storage area',
        },
        {
          name: 'firstMoveToDate',
          details: 'String',
          info: 'First move into storage area',
        },
        {
          name: 'lastEmptyDate',
          details: 'String',
          info: 'When the storage area got emptied last',
        },
        {
          name: 'lastInventoryDate',
          details: 'String',
          info: 'When the storage area got inventoried last',
        },
        {
          name: 'firstMoveToDateTime',
          details: 'String',
          info: 'First move into storage area',
        },
        {
          name: 'daysSinceFirstMove',
          details: 'String',
          info: 'How many days since first move',
        },
        {
          name: 'lastEmptyDateTime',
          details: 'String',
          info: 'When the storage area got emptied last',
        },
        {
          name: 'lastInventoryDateTime',
          details: 'String',
          info: 'When the storage area got inventoried last',
        },
      ],
    },

    {
      name: 'product',
      children: [
        {
          name: 'id',
          details: 'Number',
          info: 'Id of first product in storage area',
        },
        {
          name: 'name',
          details: 'String',
          info: 'Name of product',
        },
        {
          name: 'multiple',
          details: 'Boolean',
          info: 'True when there are multiple products in this storage area',
        },
        {
          name: 'color',
          details: 'Color',
          info: 'Color of first product in storage area',
        },
      ],
    },

    {
      name: 'productGroup',
      children: [
        {
          name: 'name',
          details: 'String',
          info: 'Name of the product group',
        },
        {
          name: 'color',
          details: 'Color',
          info: 'Color of the product group',
        },
      ],
    },

    {
      name: 'storageAreaType',
      children: [
        {
          name: 'producer',
          details: '',
          info: '',
        },
        {
          name: 'consumer',
          details: '',
          info: '',
        },
        {
          name: 'vehicle',
          details: '',
          info: '',
        },
        {
          name: 'logSorterIntake',
          details: '',
          info: '',
        },
        {
          name: 'sawLineIntake',
          details: '',
          info: '',
        },
        {
          name: 'logSorterBin',
          details: '',
          info: '',
        },
        {
          name: 'sortedPile',
          details: '',
          info: '',
        },
        {
          name: 'unsortedPile',
          details: '',
          info: '',
        },
        {
          name: 'truck',
          details: '',
          info: '',
        },
        {
          name: 'train',
          details: '',
          info: '',
        },
        {
          name: 'bulk',
          details: '',
          info: '',
        },
      ],
    },

    {
      name: 'storageAreaStatus',
      children: [
        {
          name: 'undefined',
          details: '',
          info: '',
        },
        {
          name: 'empty',
          details: '',
          info: '',
        },
        {
          name: 'inuse',
          details: '',
          info: '',
        },
        {
          name: 'full',
          details: '',
          info: '',
        },
      ],
    },

    {
      name: 'selected',
      details: 'Boolean',
      info: 'Shape is selected',
    },
    {
      name: 'inside',
      details: 'Boolean',
      info: 'Vehicle is inside of storage area',
    },
    {
      name: 'color',
      details: 'Color',
      info: 'Color of the shape',
    },

    {
      name: 'vehicle',
      children: [
        {
          name: 'empty',
          details: 'Boolean',
          info: 'Vehicle is empty',
        },
      ],
    },
  ],
};

export const language = createLanguage(formulaParser, languageDescription);
